import { useCallback, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { Trans, useTranslation } from '@lyearn/i18n';
import { Box, useSnackbar } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { PreserveModalForm, usePreserveModalForm } from '@/components/PreserveModalForm';
import { CreatePraiseInput } from '@/types/schema';

import usePraiseMutations from '../../hooks/usePraisesMutations';
import GivePraiseForm from './components/GivePraiseForm';
import { GivePraiseModalProps } from './types';

const GivePraiseModal: React.FC<GivePraiseModalProps> = (props) => {
  const { onClose, onSuccess, praise, users, initialFormValues } = props;
  const { t } = useTranslation(['Praises']);
  const { showSnackbar } = useSnackbar();

  const { onFormChange, onCloseModal, modalClosePrompt } = usePreserveModalForm({
    onClose,
  });

  const [formikSubmit, setFormikSubmit] = useState<any>();
  const [isSubmitting, { on }] = useToggle();

  const { onCreatePraise } = usePraiseMutations();

  const onSubmit = useCallback(
    async (values: CreatePraiseInput) => {
      on();
      const { response } = await onCreatePraise(values);
      if (!response?.error) {
        const receiversCount = response.data?.createPraise.praise?.receiverUsers.length || 0;
        showSnackbar({
          message: (
            <Trans
              i18nKey={receiversCount > 1 ? 'Praises:PraiseSharedMultiple' : 'Praises:PraiseShared'}
              values={{
                name: response.data?.createPraise.praise?.receiverUsers?.[0].name,
                count: receiversCount - 1,
              }}
            />
          ),
          iconStatus: 'success',
        });
        onSuccess?.();
      }
      onClose();
    },
    [on, onClose, onCreatePraise, onSuccess, showSnackbar],
  );

  return (
    <PreserveModalForm {...modalClosePrompt}>
      <Modal
        open
        action={{
          text: t('Common:Message.Post'),
          onSubmit: formikSubmit,
          isSubmitting,
          disabled: isSubmitting,
        }}
        header={{
          title: t('Praises:Form.GivePraise'),
          description: t('Praises:Form.GivePraiseModalDescription'),
        }}
        variant={ModalVariant.FullHeight}
        onClose={onCloseModal}>
        <Box className="px-32 pb-32 pt-8">
          <GivePraiseForm
            initialFormValues={initialFormValues}
            praise={praise}
            setFormikSubmit={setFormikSubmit}
            users={users}
            onChange={onFormChange}
            onSubmit={onSubmit}
          />
        </Box>
      </Modal>
    </PreserveModalForm>
  );
};

export default GivePraiseModal;
